// Incident War Rooms — a structured coordination layer on top of a channel:
// severity, a live status, an incident commander, a chronological timeline, and
// notes / action items. Self-contained: fetches its own data and listens on the
// channel's realtime stream, so it drops into the channel view with a one-line
// injection and touches no shared CSS (inline, glass-themed styles).
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { api, Incident, IncidentSeverity, IncidentStatus, PublicUser } from './api';
import { activeTz } from './prefs';
import { sharedSub } from './realtime';
import { Select } from './Select';

const SEV_META: Record<IncidentSeverity, { label: string; bg: string; fg: string }> = {
  sev1: { label: 'SEV1', bg: '#e5484d', fg: '#fff' },
  sev2: { label: 'SEV2', bg: '#f76808', fg: '#fff' },
  sev3: { label: 'SEV3', bg: '#f5d90a', fg: '#1b1300' },
  sev4: { label: 'SEV4', bg: '#8b8d98', fg: '#fff' },
};
const STATUS_META: Record<IncidentStatus, { label: string; color: string }> = {
  open: { label: 'Investigating', color: '#e5484d' },
  mitigating: { label: 'Mitigating', color: '#f76808' },
  monitoring: { label: 'Monitoring', color: '#0091ff' },
  resolved: { label: 'Resolved', color: '#30a46c' },
};
const STATUS_FLOW: IncidentStatus[] = ['open', 'mitigating', 'monitoring', 'resolved'];
const SEVS: IncidentSeverity[] = ['sev1', 'sev2', 'sev3', 'sev4'];
const KIND_ICON: Record<string, string> = {
  declared: '🚨', status: '🔄', severity: '⚠️', commander: '⭐', role: '👥',
  note: '📝', action: '✅', resolved: '🟢', reopened: '🔁',
};

function fmtTime(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: activeTz(),
  });
}
function sinceLabel(iso?: string | null): string {
  if (!iso) return '';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

const pill: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 700,
  fontSize: 11, letterSpacing: 0.4, padding: '2px 8px', borderRadius: 6, lineHeight: 1.6,
};
const chipBtn: CSSProperties = {
  border: '1px solid var(--fx-hair)', background: 'transparent', color: 'var(--fx-text)',
  borderRadius: 7, padding: '4px 9px', fontSize: 12, cursor: 'pointer', lineHeight: 1.4,
};

// ── The pinned banner + expandable drawer ─────────────────────────────────────
export function IncidentBanner({
  channelId, workspaceId, meId, members, reloadKey, onToast,
}: {
  channelId: string;
  workspaceId: string;
  meId?: string;
  members: PublicUser[];
  reloadKey: number;
  onToast: (t: string) => void;
}) {
  const [inc, setInc] = useState<Incident | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [noteKind, setNoteKind] = useState<'note' | 'action'>('note');
  const summaryRef = useRef<string>('');

  useEffect(() => {
    let alive = true;
    api.channelIncident(channelId).then((d) => { if (alive) setInc(d); }).catch(() => {});
    return () => { alive = false; };
  }, [channelId, reloadKey]);

  // Live: any incident.update on this channel re-pulls the full record (timeline included).
  useEffect(() => {
    const off = sharedSub(`channel:${channelId}`, (ctx: any) => {
      if (ctx?.data?.type === 'incident.update') {
        api.channelIncident(channelId).then(setInc).catch(() => {});
      }
    });
    return off;
  }, [channelId]);

  const patch = useCallback(async (p: Parameters<typeof api.updateIncident>[1]) => {
    if (!inc) return;
    setBusy(true);
    try { setInc(await api.updateIncident(inc.id, p)); }
    catch (e: any) { onToast(e?.message || 'Update failed'); }
    finally { setBusy(false); }
  }, [inc, onToast]);

  if (!inc) return null;
  const sev = SEV_META[inc.severity] || SEV_META.sev2;
  const st = STATUS_META[inc.status] || STATUS_META.open;
  const resolved = inc.status === 'resolved';
  const nameOf = (id?: string | null): string => {
    if (!id) return 'Unassigned';
    const m = members.find((x) => x.id === id);
    return inc.people?.[id]?.name || m?.name || m?.email || 'Unknown';
  };

  const addNote = async () => {
    const t = note.trim();
    if (!t || !inc) return;
    setBusy(true);
    try { setInc(await api.addIncidentEvent(inc.id, noteKind, t)); setNote(''); }
    catch (e: any) { onToast(e?.message || 'Could not add entry'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{
      borderBottom: '1px solid var(--fx-hair)',
      background: resolved
        ? 'linear-gradient(0deg, var(--fx-glass), var(--fx-glass))'
        : 'var(--fx-glass)',
      backdropFilter: 'blur(10px)',
      boxShadow: `inset 4px 0 0 ${resolved ? STATUS_META.resolved.color : sev.bg}`,
    }}>
      {/* Compact row — always visible */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', flexWrap: 'wrap' }}>
        <span style={{ ...pill, background: sev.bg, color: sev.fg }}>🚨 {sev.label}</span>
        <span style={{ ...pill, background: 'transparent', color: st.color, border: `1px solid ${st.color}55` }}>
          <span style={{ width: 7, height: 7, borderRadius: 99, background: st.color, display: 'inline-block' }} />
          {st.label}
        </span>
        <span style={{ fontWeight: 650, color: 'var(--fx-text)', fontSize: 14 }}>{inc.title}</span>
        <span style={{ color: 'var(--fx-muted)', fontSize: 12 }}>
          · ⭐ {nameOf(inc.commander_id)}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ color: 'var(--fx-muted)', fontSize: 12 }} title={`Started ${fmtTime(inc.started_at)}`}>
          {resolved ? `resolved · ${sinceLabel(inc.resolved_at)} ago` : `⏱ ${sinceLabel(inc.started_at)}`}
        </span>
        <button type="button" style={chipBtn} onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide' : 'Manage'} ▾
        </button>
      </div>

      {/* Drawer — controls + timeline */}
      {open && (
        <div style={{ padding: '4px 16px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
            {/* Status stepper */}
            <div>
              <div style={labelStyle}>Status</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {STATUS_FLOW.map((s) => {
                  const active = inc.status === s;
                  const c = STATUS_META[s].color;
                  return (
                    <button key={s} type="button" disabled={busy}
                      onClick={() => !active && patch({ status: s })}
                      style={{ ...chipBtn, cursor: active ? 'default' : 'pointer',
                        borderColor: active ? c : 'var(--fx-hair)',
                        background: active ? `${c}22` : 'transparent',
                        color: active ? c : 'var(--fx-text)', fontWeight: active ? 700 : 500 }}>
                      {STATUS_META[s].label}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Severity */}
            <div>
              <div style={labelStyle}>Severity</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {SEVS.map((s) => {
                  const active = inc.severity === s;
                  const m = SEV_META[s];
                  return (
                    <button key={s} type="button" disabled={busy}
                      onClick={() => !active && patch({ severity: s })}
                      style={{ ...chipBtn, cursor: active ? 'default' : 'pointer',
                        borderColor: active ? m.bg : 'var(--fx-hair)',
                        background: active ? m.bg : 'transparent',
                        color: active ? m.fg : 'var(--fx-text)', fontWeight: active ? 700 : 500 }}>
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Commander */}
            <div>
              <div style={labelStyle}>Commander</div>
              <Select value={inc.commander_id || ''} disabled={busy} style={{ minWidth: 150 }}
                onChange={(v) => patch({ commander_id: v || null })}
                options={[{ value: '', label: 'Unassigned' }, ...members.map((m) => ({ value: m.id, label: `${m.name || m.email}${m.id === meId ? ' (you)' : ''}` }))]} />
            </div>
          </div>

          {/* Resolution summary / postmortem seed */}
          <div>
            <div style={labelStyle}>Summary {resolved ? '' : '(fill in as you go)'}</div>
            <textarea
              defaultValue={inc.summary || ''}
              placeholder="What happened, impact, and how it was resolved…"
              onChange={(e) => { summaryRef.current = e.target.value; }}
              onBlur={() => { if (summaryRef.current !== (inc.summary || '')) patch({ summary: summaryRef.current }); }}
              style={{ width: '100%', minHeight: 46, resize: 'vertical', boxSizing: 'border-box',
                border: '1px solid var(--fx-hair)', borderRadius: 8, background: 'transparent',
                color: 'var(--fx-text)', font: 'inherit', fontSize: 13, padding: '7px 9px' }} />
          </div>

          {/* Timeline */}
          <div>
            <div style={labelStyle}>Timeline</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 210, overflowY: 'auto' }}>
              {(inc.timeline || []).map((e) => (
                <div key={e.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 13, padding: '2px 0' }}>
                  <span style={{ width: 18, textAlign: 'center', flexShrink: 0 }}>{KIND_ICON[e.kind] || '•'}</span>
                  <span style={{ color: 'var(--fx-text)' }}>{e.body}</span>
                  <span style={{ color: 'var(--fx-faint)', fontSize: 11, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                    {fmtTime(e.created_at)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Add note / action */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <div style={{ display: 'flex', border: '1px solid var(--fx-hair)', borderRadius: 7, overflow: 'hidden' }}>
              {(['note', 'action'] as const).map((k) => (
                <button key={k} type="button" onClick={() => setNoteKind(k)}
                  style={{ border: 'none', padding: '5px 9px', fontSize: 12, cursor: 'pointer',
                    background: noteKind === k ? 'var(--fx-accent)' : 'transparent',
                    color: noteKind === k ? '#1b1300' : 'var(--fx-muted)', fontWeight: noteKind === k ? 700 : 500 }}>
                  {k === 'note' ? '📝 Note' : '✅ Action'}
                </button>
              ))}
            </div>
            <input value={note} onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }}
              placeholder={noteKind === 'note' ? 'Log an update…' : 'Add an action item…'}
              style={{ flex: 1, border: '1px solid var(--fx-hair)', borderRadius: 7, background: 'transparent',
                color: 'var(--fx-text)', font: 'inherit', fontSize: 13, padding: '6px 9px' }} />
            <button type="button" style={{ ...chipBtn, background: 'var(--fx-accent)', color: '#1b1300', border: 'none', fontWeight: 700 }}
              disabled={busy || !note.trim()} onClick={addNote}>Add</button>
          </div>
        </div>
      )}
    </div>
  );
}

const labelStyle: CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
  color: 'var(--fx-muted)', marginBottom: 6,
};

// ── Declare-incident modal (triggered from the channel kebab) ─────────────────
export function DeclareIncidentModal({
  open, workspaceId, channelId, channelName, onClose, onDeclared,
}: {
  open: boolean;
  workspaceId: string;
  channelId: string;
  channelName?: string;
  onClose: () => void;
  onDeclared: () => void;
}) {
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState<IncidentSeverity>('sev2');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { if (open) { setTitle(''); setSeverity('sev2'); setErr(''); } }, [open]);
  if (!open) return null;

  const submit = async () => {
    if (!title.trim()) { setErr('Give the incident a title.'); return; }
    setBusy(true); setErr('');
    try {
      await api.declareIncident(workspaceId, { title: title.trim(), severity, channel_id: channelId });
      onDeclared();
    } catch (e: any) { setErr(e?.message || 'Could not declare the incident.'); setBusy(false); }
  };

  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal" onClick={(e) => e.stopPropagation()} style={{ width: 'min(460px, 92vw)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}>
          <span style={{ fontSize: 20 }}>🚨</span>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 750 }}>Declare an incident</h3>
        </div>
        <p style={{ margin: '0 0 16px', color: 'var(--fx-muted)', fontSize: 13 }}>
          Turns <b>#{channelName || 'this channel'}</b> into a war room — severity, a live status,
          a commander, and a shared timeline.
        </p>

        <label style={labelStyle}>What's going on?</label>
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="e.g. Checkout failing for all users"
          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--fx-hair)', borderRadius: 9,
            background: 'transparent', color: 'var(--fx-text)', font: 'inherit', fontSize: 14,
            padding: '9px 11px', marginBottom: 16 }} />

        <label style={labelStyle}>Severity</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          {SEVS.map((s) => {
            const m = SEV_META[s];
            const active = severity === s;
            return (
              <button key={s} type="button" onClick={() => setSeverity(s)}
                style={{ flex: 1, padding: '8px 0', borderRadius: 9, cursor: 'pointer', fontWeight: 700, fontSize: 13,
                  border: `1px solid ${active ? m.bg : 'var(--fx-hair)'}`,
                  background: active ? m.bg : 'transparent', color: active ? m.fg : 'var(--fx-text)' }}>
                {m.label}
              </button>
            );
          })}
        </div>
        <p style={{ margin: '-10px 0 16px', color: 'var(--fx-faint)', fontSize: 11.5 }}>
          {severity === 'sev1' && 'Critical — full outage / data at risk.'}
          {severity === 'sev2' && 'Major — significant degradation.'}
          {severity === 'sev3' && 'Minor — limited impact.'}
          {severity === 'sev4' && 'Low — cosmetic or contained.'}
        </p>

        {err && <div style={{ color: 'var(--fx-alert)', fontSize: 13, marginBottom: 12 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={busy}
            style={{ ...chipBtn, padding: '8px 14px' }}>Cancel</button>
          <button type="button" onClick={submit} disabled={busy}
            style={{ padding: '8px 16px', borderRadius: 9, border: 'none', cursor: 'pointer',
              background: '#e5484d', color: '#fff', fontWeight: 700, fontSize: 13.5 }}>
            {busy ? 'Declaring…' : '🚨 Declare'}
          </button>
        </div>
      </div>
    </div>
  );
}
